// import supabase from '../utils/supabaseClient';
import { FormState, BorrowerData, RentalProperty, Income } from '../pages/BorrowerForm';
import proxyClient from '../utils/proxyClient';

export interface SubmissionResponse {
  success: boolean;
  error?: string;
  formId?: string;
}

// Interface for the service payload
export interface BorrowerFormServicePayload extends Omit<FormState, 'rentalProperties' | 'incomes'> {
  // Ensure rentalProperties and incomes are optional or handled correctly if needed
  rentalProperties?: { [key: string]: RentalProperty }; 
  incomes?: { [key: string]: Income }; 
  borrowers: { [key: string]: BorrowerData };
  conditions: { [key: string]: boolean };
}

// Service function to handle form submission
export const submitBorrowerForm = async (formData: BorrowerFormServicePayload): Promise<SubmissionResponse> => {
  try {
    // Build the payload similar to how it was sent to the RPC function
    const payload = {
      p_form_data: {
        deal_number: formData.dealNumber,
        deal_type: formData.dealType,
        closing_date: formData.closingDate,
        cof: formData.cof,
        bfs_stage: formData.bfsStage,
        property_watchlist_status: formData.propertyWatchlistStatus,
        employer_watchlist_status: formData.employerWatchlistStatus,
        match_files_status: formData.matchFilesStatus,
        match_files_notes: formData.matchFilesNotes,
        property_tax_adjustment: formData.propertyTaxAdjustment,
        // Removed fields that no longer exist in the deals table
        // deal_borrower_relationship_status: formData.dealBorrowerRelationshipStatus,
        // deal_borrower_relationship_other: formData.dealBorrowerRelationshipOther,
        property_value_status: formData.propertyValueStatus,
        property_value_other_notes: formData.propertyValueOtherNotes,
        mortgage_amount: formData.mortgageAmount,
        mtg_type: formData.mtgType,
        ltv: formData.ltv,
        eto_checked: formData.etoChecked,
        eto_notes: formData.etoNotes,
        term: formData.term,
        rate: formData.rate,
        amortization: formData.amortization,
        ltv_restrictions: formData.ltvRestrictions,
        property_info: formData.propertyInfo,
        province: formData.province,
        savings_status: formData.savingsStatus,
        since_date_status: formData.sinceDateStatus,
        since_date_other_notes: formData.sinceDateOtherNotes,
        elapsed_time: formData.elapsedTime,
        start_time: formData.startTime,
        end_time: formData.endTime,
        downpayment_source: formData.downpaymentSource,
        downpayment_source_notes: formData.downpaymentSourceNotes,
        downpayment_amount: formData.downpaymentAmount,
        giftor: formData.giftor,
        inheriting_from: formData.inheritingFrom
      },
      p_borrowers: Object.entries(formData.borrowers).map(([_, borrower]: [string, BorrowerData], index) => ({
        borrower_number: index + 1,
        name: borrower.name,
        aka: borrower.aka,
        has_aka: borrower.hasAKA,
        aka_reason: borrower.akaReason,
        dob: borrower.dob,
        has_sin: borrower.hasSIN,
        relationship_status: borrower.relationshipStatus,
        relationship_other: borrower.relationshipOther,
        dob_merlin: borrower.dobMerlin,
        sin_cb_status: borrower.sinCbStatus,
        beacon: borrower.beacon,
        date_cb_pulled: borrower.dateCBPulled,
        agent_for_cb: borrower.agentForCB,
        inquiries: borrower.inquiries,
        mtg_inquiry_reason: borrower.mtg_inquiry_reason,
        other_mtg_inquiry_reason: borrower.other_mtg_inquiry_reason,
        address: borrower.address,
        credit_history_notes: borrower.creditHistoryNotes,
        other_credit_history_notes: borrower.otherCreditHistoryNotes,
        adverse_media: borrower.adverseMedia,
        warning_noted: borrower.warningNoted,
        warning_notes: borrower.warningNotes,
        employment_on_cb: borrower.employmentOnCB,
        repayment_history: borrower.repaymentHistory,
        other_repayment_history: borrower.otherRepaymentHistory,
        since_date_status: borrower.sinceDateStatus,
        since_date_other_notes: borrower.sinceDateOtherNotes,
        overall_profile: borrower.overallProfile,
        overall_profile_other: borrower.overallProfileOther,
        pr_rt: borrower.pr_rt,
        rt_description: borrower.rt_description,
        child_support: borrower.childSupport,
        no_child_support: borrower.noChildSupport,
        stat_dec_for_separation: borrower.statDecForSeparation,
        bankruptcy: borrower.bankruptcy,
        cp_discharged: borrower.cpDischarged,
        cp_active: borrower.cpActive,
        not_on_title: borrower.notOnTitle
      })),
      p_rental_properties: Object.entries(formData.rentalProperties || {}).map(([_, property]: [string, RentalProperty], index) => ({
        property_number: index + 1,
        new_build: property.newBuild,
        purchase_price: property.purchasePrice,
        add_three_percent: property.addThreePercent,
        add_one_percent_pp: property.addOnePercentPP,
        calculate_surplus: property.calculateSurplus,
        address: property.address,
        property_type: property.propertyType,
        rental_income: property.rentalIncome,
        property_tax: property.propertyTax,
        condo_fees: property.condoFees,
        mortgage_payment: property.mortgagePayment,
        mortgage_amount: property.mortgageAmount,
        heat: property.heat,
        owned_percent_b1: property.ownedPercentB1,
        owned_percent_b2: property.ownedPercentB2,
        owned_percent_b3: property.ownedPercentB3,
        owned_percent_b4: property.ownedPercentB4,
        rental_surplus_deficit: property.rentalSurplusDeficit,
        use_ninety_five_percent_income: property.useNinetyFivePercentIncome,
        calculated_tax_plus_3_percent: property.calculatedTaxPlus3Percent,
        calculated_one_percent_pp: property.calculatedOnePercentPP,
        market_rent: property.marketRent,
        lease_agreement: property.leaseAgreement,
        notice_to_vacate: property.noticeToVacate,
        being_sold_for_dp: property.beingSoldForDP,
        refi_for_dp: property.refiForDP,
        bridge_ab: property.bridgeAB,
        bridge_bc: property.bridgeBC,
        out_of_province: property.outOfProvince
      })),
      p_incomes: Object.entries(formData.incomes || {}).map(([_, income]: [string, Income], index) => ({
        income_number: index + 1,
        incomeDescription: income.incomeDescription,
        annual_income: income.annualIncome,
        employer: income.employer,
        job_description: income.job_description,
        tenure: income.tenure,
        timeInIndustry: income.timeInIndustry,
        bfsYn: income.bfsYn,
        borrowerId: income.borrowerId
      })),
      p_conditions: Object.entries(formData.conditions)
        .filter(([_, isSelected]) => isSelected)
        .map(([conditionName]) => ({
          condition_name: conditionName,
          is_selected: true
        }))
    };

    // Send to backend API endpoint instead of direct RPC call
    // Get the token using the same key format as proxyClient
    const key = 'sb-' + process.env.REACT_APP_SUPABASE_URL?.replace('https://', '') + '-auth-token';
    const tokenData = localStorage.getItem(key);
    let token;
    
    try {
      // Parse the token data, which is a JSON object containing access_token
      const parsedTokenData = JSON.parse(tokenData || '{}');
      token = parsedTokenData.access_token;
      
      if (!token) {
        throw new Error('No access token found in session data');
      }
    } catch (error) {
      console.error('Error parsing token data:', error);
      throw new Error('Invalid authentication token');
    }
    
    const response = await fetch('/api/borrower-data/submit-form', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit form');
    }

    const result = await response.json();
    return {
      success: true,
      formId: result.formId
    };
  } catch (error: any) {
    console.error('Error submitting form:', error);
    return {
      success: false,
      error: error.message || 'An unknown error occurred while submitting the form'
    };
  }
};
